import React, { useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { CloseButton } from '@rsos/components/capstone/base';
import {
  AGENT_511_MAP_CARD,
  AVIVE_APP_LAUNCHER_MAP_CARD,
  AXON_MAP_CARD,
  DYNAMIC_LAYERS_CARD,
  RADE_MAP_CARD,
  STATIC_LAYERS_CARD,
  closeAllCards,
  EAGLE_EYE_MAP_CARD,
} from '@rsos/reduxDataStore/src/features/mapCard/mapCardSlice';
import usePortalType from '@rsos/utils/usePortalType';
import AviveContainer, {
  hidePlaceholders,
} from '../JurisdictionView/AviveAED/AviveContainer';
import RADECardContainer from '../JurisdictionView/RADECard/RADECardContainer';
import { hide } from '../JurisdictionView/RADECard/radeSlice';
import { reset } from '../JurisdictionView/UniversalSearch/searchSlice';
import { trackCloseRADECard } from '../JurisdictionView/jurisdictionViewTrackings';
import TextTo911Container from '../TextTo911/TextTo911Container';
import DynamicLayerCard from './DynamicLayerCard';
import { StyledContainer } from './MapCard.styles';
import StaticLayerCard from './StaticLayerCard';

/**
 * Container component with the app logic to render different map cards
 * @param {Boolean} hasEsriMapAccess - Flag for if user has access to Esri maps
 * @param {Object} mapRef - mapRef used to calculate the height of the map
 */
const MapCard = ({ hasEsriMapAccess, mapRef, setMafError, setADRError }) => {
  const dispatch = useDispatch();
  const { irpVersion } = useSelector(state => state.sinatra.irp);
  const selectedLocation = useSelector(
    state => state.gisSearch.selectedLocation,
  );
  const { portalType } = usePortalType();

  const { cards } = useSelector(state => state.mapCard);

  /**
   * Handle closing all cards
   * @param {Function} callbackFn - Callback function for custom handling when
   * closing certain cards
   */
  const handleCloseCard = useCallback(
    (callbackFn = () => {}) => {
      dispatch(closeAllCards());
      callbackFn();
    },
    [dispatch],
  );

  const handleCloseRadECard = useCallback(() => {
    dispatch(hide());
    // Track when closing RADECard since it's not explicitly closing with the RADECard's close button
    if (cards[RADE_MAP_CARD]) {
      trackCloseRADECard(irpVersion, portalType);
      if (!selectedLocation) {
        dispatch(reset());
      }
    }
  }, [cards, dispatch, irpVersion, portalType, selectedLocation]);

  return (
    <StyledContainer hasEsriMapAccess={hasEsriMapAccess} mapRef={mapRef}>
      <CloseButton
        absolute
        id="close-map-card-button"
        data-name="close-map-card-button"
        onClose={() => {
          hidePlaceholders();
          handleCloseCard();
          handleCloseRadECard();
        }}
        right={16}
        top={16}
        zIndex={1}
      />
      {Object.keys(cards).map(key => {
        const shouldRender = cards[key];
        if (shouldRender) {
          if (key === AGENT_511_MAP_CARD) {
            return <TextTo911Container key={key} onClose={handleCloseCard} />;
          } else if (key === AVIVE_APP_LAUNCHER_MAP_CARD) {
            return (
              <AviveContainer
                key={key}
                shouldRender={shouldRender}
                onClose={handleCloseCard}
              />
            );
          } else if (key === RADE_MAP_CARD) {
            return (
              <RADECardContainer
                key={key}
                setMafError={setMafError}
                setADRError={setADRError}
              />
            );
          } else if (key === AXON_MAP_CARD || key === EAGLE_EYE_MAP_CARD) {
            // TODO: can combine these, but due to different implementations of
            // the layers between deck.gl and google maps, it might cause
            // unexpected issues. revisit in future update
            return <DynamicLayerCard key={key} />;
          } else if (key === DYNAMIC_LAYERS_CARD) {
            return <DynamicLayerCard key={key} />;
          } else if (key === STATIC_LAYERS_CARD) {
            return <StaticLayerCard key={key} />;
          }
          return null;
        }
      })}
    </StyledContainer>
  );
};

export default React.memo(MapCard);
